import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, Eye, Sparkles, Loader2, Trash2 } from "lucide-react";
import { fmtDateTime } from "./utils";

/** Tab "Assinados" — single uploader supporting one or many files, with per-doc actions. */
export default function AssinadosTab({
  cs, uploading, analyzing,
  onUploadMultiple, onOpen, onAnalyze, onDelete,
}) {
  const fileRef = useRef(null);
  const signed = cs.signed_documents || [];

  return (
    <Card><CardContent className="p-6 space-y-4">
      <div>
        <h3 className="font-display font-semibold text-slate-900">Upload de documentos assinados</h3>
        <p className="text-xs text-slate-500 mt-1">
          Aceita PDF, fotos ou captura direta pela câmera. Envie 1 arquivo único (ex.: PDF consolidado) ou vários ao mesmo tempo.
          Você pode adicionar mais documentos, substituir ou apagar a qualquer momento.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept="application/pdf,image/*"
          multiple
          capture="environment"
          onChange={(e) => { onUploadMultiple(e.target.files); e.target.value = ""; }}
          data-testid="input-signed-multi"
        />
        <Button
          className="bg-blue-700 hover:bg-blue-800 h-11 md:h-9"
          onClick={() => fileRef.current?.click()}
          disabled={!!uploading["assinado"]}
          data-testid="btn-upload-signed-multi"
        >
          <Upload className="h-4 w-4 mr-2" />
          {uploading["assinado"] ? "Enviando..." : "Adicionar documento(s)"}
        </Button>
        <span className="text-xs text-slate-500">
          {signed.length > 0
            ? `${signed.length} documento(s) já anexado(s)`
            : "Nenhum documento anexado ainda"}
        </span>
      </div>

      <div className="border-t border-slate-200 pt-4">
        <h3 className="font-display font-semibold text-slate-900 mb-2">Documentos anexados</h3>
        {signed.length === 0 && <div className="text-sm text-slate-500">Nenhum documento assinado enviado ainda.</div>}
        <ul className="space-y-2">
          {signed.map((d) => (
            <li key={d.id} className="flex items-center justify-between border border-slate-200 rounded-md p-3 gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                  {d.label}
                  {d.ai_extracted && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider flex items-center gap-1 ${d.ai_discrepancy_count > 0 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                      <Sparkles className="h-2.5 w-2.5" />
                      {d.ai_discrepancy_count > 0 ? `${d.ai_discrepancy_count} diverg.` : "IA ✓"}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{d.original_filename} · {fmtDateTime(d.uploaded_at)} · por {d.uploaded_by_name}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                <Button size="sm" variant="outline" onClick={() => onOpen(d)} data-testid={`btn-open-signed-${d.id}`}>
                  <Eye className="h-3.5 w-3.5 mr-1" /> Abrir
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100"
                  onClick={() => onAnalyze(d)}
                  disabled={!!analyzing[d.id]}
                  data-testid={`btn-analyze-ai-${d.id}`}
                >
                  {analyzing[d.id]
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Analisando…</>
                    : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Analisar com IA</>}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => onDelete(d.id, d.original_filename || d.label)}
                  data-testid={`btn-delete-signed-${d.id}`}
                  title="Apagar este documento"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </CardContent></Card>
  );
}
